import { appData, queryArgs, env } from '../utils'

export default ( constants ) => {

	/**
	 * Get the App URL with optional path.
	 *
	 * @since 5.13.13
	 *
	 * @param   {string} path App URL path.
	 * @returns {string}      The App URL.
	 */
	const app = (path = '') => {
		let url = appData.get('omAppUrl', constants.APP_URL || 'https://app.optinmonster.com' )

		if (path) {
			if ('campaigns/' === path && !env.isEnv('production')) {
				return '/campaigns'
			}

			url += '/' + path
		}

		return url
	}

	/**
	 * Get the App URL with optional path.
	 *
	 * @since 5.13.47
	 *
	 * @param   {string} path App URL path.
	 * @returns {string}      The App URL.
	 */
	const marketing = (path = '') => {
		let url = appData.get('marketing', localStorage.getItem('marketingUrl') || constants.MARKETING_URL || 'https://optinmonster.com')

		if (path) {
			url += '/' + path
		}

		return url
	}

	/**
	 * Get the API URL with optional path.
	 *
	 * @since 5.13.13
	 *
	 * @param   {string} path API URL path.
	 * @returns {string}      The API URL.
	 */
	const api = (path = '') => {
		let url = appData.get('omAppApiUrl', constants.API_URL || app())
		url += '/v2'

		if (path) {
			url += '/' + path
		}

		return url
	}

	/**
	 * Get the TrustPulse API URL with optional path.
	 *
	 * @since 5.13.43
	 *
	 * @param   {string} path API URL path.
	 * @returns {string}      The API URL.
	 */
	const trustpulse = (path = '') => {
		let url = appData.get('tpAppUrl', localStorage.getItem('trustpulseUrl') || constants.TP_APP_URL || 'https://app.trustpulse.com')
		url += '/v1'

		if (path) {
			url += '/' + path
		}

		return url
	}

	/**
	 * Get the CDN URL with optional path.
	 *
	 * @since 5.13.16
	 *
	 * @param   {string} path API URL path.
	 * @returns {string}      The API URL.
	 */
	const cdn = (path = '') => {
		let url = appData.get('omAppCdnURL', constants.CDN_URL || 'https://a.optinmonster.com')

		if (path) {
			url += '/' + path
		}

		return url
	}

	/**
	 * Get the CDN URL for api.js.
	 *
	 * @since 5.20.65
	 *
	 * @returns {string} The CDN URL for api.js.
	 */
	const apiJs = () => {
		return appData.get('apijsUrl', constants.API_JS_URL || cdn('app/js/api.min.js'))
	}

	/**
	 * Get a edit-campaign url.
	 *
	 * @since 5.13.13
	 *
	 * @param   {string} id        Campaign slug
	 * @param   {string} path      Whether to show edit/preview/analytics path. Defaults to 'edit/'.
	 * @param   {string} returnUrl Optional return URL to add as query arg.
	 * @returns {string}           The edit-campaign url.
	 */
	const campaign = (id, path = 'edit/', returnUrl = '', isLegacy = false) => {
		const hotnow = !isLegacy ? appData.get('hotnow', localStorage.getItem('hotnowUrl') || constants.HOTNOW_URL) : false

		let url = hotnow && ('edit' === path || 0 === path.indexOf('edit/')) ? hotnow : app()

		url += '/campaigns/' + id + '/'
		url += path

		url = new URL(url)
		if (returnUrl) {
			url.searchParams.set('return', returnUrl)
		}

		return url.toString()
	}

	/**
	 * Get the new-campaign url.
	 *
	 * @since 5.18.41
	 *
	 * @param   {Object} params Optional query params.
	 * @returns {string}        The new-campaign url.
	 */
	const newCampaign = (params = {}) => {
		let url = appData.get('newCampaignUrl', localStorage.getItem('newCampaignUrl') || app() + '/campaigns/new/')

		url = new URL(url)
		if (Object.keys(params).length) {
			let p
			for (p in params) {
				url.searchParams.set(p, params[ p ])
			}
		}

		return url.toString()
	}

	/**
	 * Get the WP site URL with optional path.
	 *
	 * @since 5.20.63
	 *
	 * @param   {string} path Site URL path.
	 * @returns {string}      The site URL.
	 */
	const wp = (path = '') => {
		let url = appData.get('wpUrl', localStorage.getItem('wpUrl') || constants.WP_DEV_URL)

		if (path) {
			url += path
		}

		return url
	}

	/**
	 * Get the WP REST API URL with optional path.
	 *
	 * @todo This may have issues if the WP site is in a subdirectory,
	 * so we should pass in the site_url() instead
	 *
	 * @since 5.X.X
	 *
	 * @param   {string} path API URL path.
	 * @returns {string}      The API URL.
	 */
	const wpRest = (path = '') => {
		let url = appData.get('restUrl', localStorage.getItem('wpRestUrl') || constants.WP_REST_API_URL || wp() + 'wp-json/')

		if (path) {
			const parts = path.split('?')
			url += parts[0]
			// Let's parse the query args, and add them properly
			// (in case the rest_url already has query args)
			if (parts[1]) {
				const args = queryArgs.parse(parts[1])
				url = new URL(url)
				Object.keys(args).forEach( k => {
					url.searchParams.set(k, args[k])
				})
				url = url.toString()
			}
		}

		return url
	}

	/**
	 * Get the WP Admin URL with optional query params.
	 *
	 * @todo This may have issues if the WP site is in a subdirectory,
	 * so we should pass in the site_url() instead
	 *
	 * @since 5.20.63
	 *
	 * @param   {string} params Admin URL query params.
	 * @returns {string}      The Admin URL.
	 */
	const wpAdmin = (params = '') => {
		let url = appData.get('adminUrl', localStorage.getItem('wpAdminUrl') || constants.WP_ADMIN_API_URL || wp() + 'wp-admin/admin.php')

		if (params) {
			url += params
		}

		return url
	}

	return {
		app,
		marketing,
		api,
		trustpulse,
		cdn,
		apiJs,
		campaign,
		newCampaign,
		wp,
		wpRest,
		wpAdmin,
	}
}

